import { create } from 'zustand';
import { axiosEnquiryInstance as axios } from '../lib/axiosInstance';
import { toast } from 'react-hot-toast';

const useBlogStore = create((set, get) => ({
    posts: [],
    post: null,
    categories: [],
    tags: [],
    pagination: null,
    isLoading: false,
    error: null,

    // Fetch a list of blog posts
    fetchPosts: async (page = 1, limit = 10) => {
        set({ isLoading: true, error: null });
        try {
            const response = await axios.get(`/blogs?page=${page}&limit=${limit}`);
            if (response.data.success) {
                set({
                    posts: response.data.posts,
                    pagination: response.data.pagination,
                    isLoading: false,
                });
            } else {
                throw new Error(response.data.message || 'Failed to fetch posts');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error fetching blog posts.';
            toast.error(errorMsg);
            set({ error: errorMsg, isLoading: false, posts: [] });
        }
    },

    // Fetch a single blog post by its slug
    fetchPostBySlug: async (slug) => {
        set({ isLoading: true, error: null, post: null });
        try {
            const response = await axios.get(`/blogs/slug/${slug}`);
            if (response.data.success) {
                set({
                    post: response.data.post,
                    isLoading: false,
                });
            } else {
                throw new Error(response.data.message || `Failed to fetch post: ${slug}`);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error fetching blog post.';
            toast.error(errorMsg);
            set({ error: errorMsg, isLoading: false });
        }
    },

    // Fetch all categories
    fetchCategories: async () => {
        // Avoid refetching if already loaded
        if (get().categories.length > 0) return;

        set({ isLoading: true });
        try {
            const response = await axios.get('/blogs/categories');
            if (response.data.success) {
                set({
                    categories: response.data.categories,
                });
            } else {
                throw new Error(response.data.message || 'Failed to fetch categories');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error fetching categories.';
            toast.error(errorMsg);
            set({ error: errorMsg, categories: [] });
        } finally {
            set({ isLoading: false });
        }
    },

    // Fetch all tags
    fetchTags: async () => {
        // Avoid refetching if already loaded
        if (get().tags.length > 0) return;

        set({ isLoading: true });
        try {
            const response = await axios.get('/blogs/tags');
            if (response.data.success) {
                set({
                    tags: response.data.tags,
                });
            } else {
                throw new Error(response.data.message || 'Failed to fetch tags');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error fetching tags.';
            toast.error(errorMsg);
            set({ error: errorMsg, tags: [] });
        } finally {
            set({ isLoading: false });
        }
    },

    // Delete a single blog post by ID
    deletePost: async (id) => {
        set({ isLoading: true });
        try {
            const response = await axios.delete(`/blogs/${id}`);
            if (response.data.success) {
                set(state => ({
                    posts: state.posts.filter(post => post.id !== id),
                    isLoading: false,
                }));
                toast.success(response.data.message || 'Post deleted!');
                return { success: true };
            } else {
                throw new Error(response.data.message || 'Delete failed!');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete post.');
            set({ isLoading: false });
            return { success: false };
        }
    },
}));

export default useBlogStore;